import { useEffect, useReducer, useRef, useState } from 'react';
import DateUtils from '../../../utils/DateUtils';
import { MessageDialog } from '../../messagebox/MessageBox';
import Button from '../../shared/button/Button';
import './SearchByData.css';
import { API } from '../../../services/api/API';
import { CurrentUser } from '../../login/CurrentUser';
import { DateRange, ExtraParamsKeys, IExtraParams } from '../../../interfaces/type1/IQueryParams';
import useSocketClientStatus from "../../hooks/useSocketClientStatus";
import DateInput from './dataInput/DataInput';
import Navigator from '../../../utils/Navigator';
import { HeadersRoutes, INavMenuRoute } from '../../header/HeaderOptions';
import Cache from '../Cache';
import { IDateEnumDTO } from './IDateEnumDTO';


export interface ISearchProps {
    OnSearchButtonClick: (init: Date, end: Date, dateRange: DateRange, extraParams?: IExtraParams[]) => void;
}

const MOBILE_WIDTH = 600;


export default function SearchByData(props: ISearchProps) {

    let [options, setOptions] = useState<IDateEnumDTO[]>([]);

    let refStart = useRef<HTMLInputElement>(null);
    let refEnd = useRef<HTMLInputElement>(null);

    let [useCache, setUseCache] = useState(true);

    let [mobileStart, setMobileStart] = useState<Date | null>(null);
    let [mobileEnd, setMobileEnd] = useState<Date | null>(null);

    let mobileStartDate = mobileStart;
    let mobileEndDate = mobileEnd;

    let [selectedOption, setSelectedOption] = useState("");
    let [countExecutionTime, setCount] = useState(1);
    let [visible, setVisibility] = useState(false);
    let isDocwinOnline = useSocketClientStatus();

    let graph: INavMenuRoute | undefined = HeadersRoutes.GetRoutes().FirstOrDefault(s => s.URL == Navigator.Path());

    let refOSQuitadas = useRef<HTMLInputElement>(null);
    let refIncluirItensGratuitos = useRef<HTMLInputElement>(null);

    const cacheAPI = useRef<IDateEnumDTO[]>([]);


    useEffect(() => {

        (async () => {


            let optionsJsonArray: IDateEnumDTO[];

            if (!cacheAPI.current.Any()) {
                let getOptionsResponse: Response | undefined;

                try {
                    getOptionsResponse = await API.RequestAsync('/data/get-dates-enums', CurrentUser.Token?.Token ?? "", 'GET');

                    if (!getOptionsResponse.ok) {
                        MessageDialog.Toast("Erro ao carregar dados", await getOptionsResponse.text());
                        return;
                    }
                }
                catch {
                    MessageDialog.Toast("Erro ao carregar dados", "Não foi possivel carregar as possibilidades de busca da API");
                    return;
                }

                optionsJsonArray = await getOptionsResponse.json() as IDateEnumDTO[];
                cacheAPI.current.AddRange(optionsJsonArray);
            }
            else {
                optionsJsonArray = cacheAPI.current!;
            }

            setVisibility(true);
            setOptions(optionsJsonArray);

            let cache = Cache.GetCache();            

            if (useCache && !!cache && cache.DateRange) {
                let cachedOption = optionsJsonArray.Where(s => s.DateRange == cache!.DateRange).FirstOrDefault();              
                if (cachedOption)
                    setSelectedOption(cachedOption.DateRangeDescription);

                setUseCache(false);

                return;
            }


            let init: string;
            let end: string;

            let selected = options.Where(s => s.DateRangeDescription == selectedOption).FirstOrDefault();

            if (useCache && !!cache && cache.DateRange) {
                let cachedOption = options.Where(s => s.DateRange == cache!.DateRange).FirstOrDefault();

                if (cachedOption) {
                    setSelectedOption(cachedOption.DateRangeDescription);
                    selected = cachedOption;
                }

                setUseCache(false);

                return;
            }

            if (!selected) {
                init = DateUtils.CastDateToAPIString(new Date());
                end = DateUtils.CastDateToAPIString(new Date());


                if (!!cache) {
                    try {


                        init = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(cache.Start));
                        end = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(cache.End));

                        if (!isDocwinOnline && countExecutionTime > 2) {

                            setSelectedOption("Hoje");
                        }

                    } catch { }
                }
                else if ((countExecutionTime > 3 && !isDocwinOnline) || !cache) {
                    setSelectedOption("Hoje");
                }

            }
            else {

                init = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(selected.Start as any as string));
                end = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(selected.End as any as string));
            }

            if (window.screen.availWidth <= MOBILE_WIDTH) {

                setMobileStart(DateUtils.CastStringToDate(init));
                setMobileEnd(DateUtils.CastStringToDate(end));
                mobileStartDate = DateUtils.CastStringToDate(init);
                mobileEndDate = DateUtils.CastStringToDate(end);

            } else {

                if (refStart.current)
                    refStart.current.value = init;

                if (refEnd.current)
                    refEnd.current!.value = end;
            }

            setCount(p => p + 1);

        })()

    }, [selectedOption, isDocwinOnline]);




    let clickHandler = () => {

        let init: string;
        let end: string;
        let dateRange: number | undefined = undefined;
        if (!isDocwinOnline) {
            let selected = options.Where(s => s.DateRangeDescription == selectedOption).FirstOrDefault();

            if (!selected) {

                MessageDialog.Toast("Atenção", "Seleciona um período para pesquisa");
                return;

            }
            else {
                dateRange = selected.DateRange;
                init = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(selected.Start as any as string));
                end = DateUtils.CastDateToAPIString(DateUtils.CastStringToDate(selected.End as any as string));
            }

        } else {

            dateRange = DateRange.UNDEFINED;

            if (window.screen.availWidth <= MOBILE_WIDTH) {

                init = mobileStartDate ? DateUtils.CastDateToAPIString(mobileStartDate) : "";
                end = mobileEndDate ? DateUtils.CastDateToAPIString(mobileEndDate) : "";

            } else {

                init = refStart.current!.value;
                end = refEnd.current!.value;
            }
        }


        if (!init) {
            MessageDialog.Toast("Atenção", "Seleciona uma data para inicio da pesquisa");
            return;
        }

        if (!end) {
            MessageDialog.Toast("Atenção", "Seleciona uma data para o final da pesquisa");
            return;
        }


        if (DateUtils.GerDiffDays(DateUtils.CastStringToDate(init), DateUtils.CastStringToDate(end)) < 0) {
            MessageDialog.Toast("Atenção", "A data final não deve ser menor que a data incial de pesquisa");
            return;
        }


        if (DateUtils.GerDiffDays(DateUtils.CastStringToDate(init), DateUtils.CastStringToDate(end)) > 30) {
            MessageDialog.Toast("Atenção", "Não é permitido pesquisas maiores que 30 dias");
            return;
        }

        dateRange = options.Any(s =>
            DateUtils.CastStringToDate(s.Start.toString()).toString() == DateUtils.CastStringToDate(init).toString() &&
            DateUtils.CastStringToDate(s.End.toString()).toString() == DateUtils.CastStringToDate(end).toString()) ?
            options.First(s =>
                DateUtils.CastStringToDate(s.Start.toString()).toString() == DateUtils.CastStringToDate(init).toString() &&
                DateUtils.CastStringToDate(s.End.toString()).toString() == DateUtils.CastStringToDate(end).toString()).DateRange : DateRange.UNDEFINED

        Cache.SetCache(
            {
                Start: init,
                End: end,
                DateRange: dateRange
            });

        if (dateRange == DateRange.UNDEFINED)
            setSelectedOption("");

        props.OnSearchButtonClick(DateUtils.CastStringToDate(init), DateUtils.CastStringToDate(end), dateRange, getExtraParams());

    }


    const getExtraParams = (): IExtraParams[] => {

        let extraParams: IExtraParams[] = [];


        if (!graph || !graph.ExtraParams || !graph.ExtraParams.Any() || !isDocwinOnline)
            return [];

        if (graph.ExtraParams.Any(s => s.Key == ExtraParamsKeys.OS_QUITADAS_APENAS))
            extraParams.Add({
                Key: ExtraParamsKeys.OS_QUITADAS_APENAS,
                Value: refOSQuitadas.current ? refOSQuitadas.current.checked : undefined
            });

        if (graph.ExtraParams.Any(s => s.Key == ExtraParamsKeys.INCLUIR_ITENS_GRATUITOS))
            extraParams.Add({
                Key: ExtraParamsKeys.INCLUIR_ITENS_GRATUITOS,
                Value: refIncluirItensGratuitos.current ? refIncluirItensGratuitos.current.checked : undefined
            });


        return extraParams;
    }


    if (!visible)
        return <></>

    return (
        <div className='SearchByDataComponentSearch'>

            {
                options && options.Any() && (
                    <div className='containerOptions'>

                        {
                            options.Select(s => (
                                <div key={s.DateRangeDescription}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            value={s.DateRangeDescription}
                                            checked={s.DateRangeDescription == selectedOption}
                                            onChange={() => setSelectedOption(s.DateRangeDescription)}
                                        />
                                        {s.DateRangeDescription}
                                    </label>
                                </div>
                            ))
                        }
                    </div>
                )
            }


            {

                window.screen.availWidth > MOBILE_WIDTH && isDocwinOnline && (<>
                    <div className='SearchByDataComponentSearchInputs'><label htmlFor="name='data-text-inicio'">Inicio</label>
                        <input name='data-text-inicio' type="date" max={'9999-12-31'} defaultValue={DateUtils.CastDateToAPIString(new Date())}
                            ref={refStart} />
                    </div>
                    <div className='SearchByDataComponentSearchInputs'><label htmlFor="name='data-text-end'">Final</label>
                        <input name='data-text-end' type="date" max={'9999-12-31'} defaultValue={DateUtils.CastDateToAPIString(new Date())}
                            ref={refEnd} />
                    </div></>
                )

            }


            {
                window.screen.availWidth <= MOBILE_WIDTH && isDocwinOnline && (<>
                    <div className='SearchByDataComponentSearchInputs'><label htmlFor="name='data-text-inicio'">Início</label>
                        <DateInput
                            defaultValue={mobileStart ?? new Date()}
                            onChange={(date) => {
                                mobileStartDate = date;

                                if (date)
                                    setMobileStart(date);
                            }}

                        />

                    </div>
                    <div className='SearchByDataComponentSearchInputs'><label htmlFor="name='data-text-end'">Final</label>
                        <DateInput
                            defaultValue={mobileEnd ?? new Date()}
                            onChange={(date) => {
                                mobileEndDate = date;

                                if (date)
                                    setMobileEnd(date);
                            }}
                        />
                    </div></>
                )
            }

            {
                isDocwinOnline && graph && graph.ExtraParams && graph.ExtraParams.Any() &&


                <div className='containerOptions'>

                    {
                        graph.ExtraParams.Any(s => s.Key == ExtraParamsKeys.OS_QUITADAS_APENAS) &&
                        <label style={{ display: 'none' }}>
                            <input ref={refOSQuitadas}
                                type="checkbox"
                                defaultChecked={!!graph.ExtraParams.FirstOrDefault(s => s.Key == ExtraParamsKeys.OS_QUITADAS_APENAS)?.Value}
                                hidden
                            />
                            {graph.ExtraParams.FirstOrDefault(s => s.Key == ExtraParamsKeys.OS_QUITADAS_APENAS)?.Label ?? "Utilizar apenas OSs quitadas"}
                        </label>

                    }

                    {
                        graph.ExtraParams.Any(s => s.Key == ExtraParamsKeys.INCLUIR_ITENS_GRATUITOS) &&
                        <label>
                            <input ref={refIncluirItensGratuitos}
                                type="checkbox"
                                defaultChecked={!!graph.ExtraParams.FirstOrDefault(s => s.Key == ExtraParamsKeys.INCLUIR_ITENS_GRATUITOS)?.Value}
                            />
                            {graph.ExtraParams.FirstOrDefault(s => s.Key == ExtraParamsKeys.INCLUIR_ITENS_GRATUITOS)?.Label ?? "Incluir itens gratuitos"}
                        </label>
                    }

                </div>

            }


            <Button Text='Pesquisar' Type='Save' OnClickEventHandler={() => { clickHandler() }} />
        </div>
    )
}